import { useContext, useState } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";

import HeadlessDialog from "@components/Dialog";
import ScrollableModalBody from "@components/ScrollableModalBody";
import { IBankAccountDTO } from "@interfaces/IBankAccountDTO";
import { SnackbarContext } from "@components/constant_components/providers/SnackbarProvider";
import Button from "@components/Button";
import FormikInput from "@components/form/FormikInput";
import BankAccountService from "@service/BankAccountService";
import Spinner from "@components/Spinner";

interface IBankAccountEditProps {
  id: number;
  selectedBankAccount: IBankAccountDTO | null;
  isOpen: boolean;
  onClose: () => void;
}

interface IBankAccountForm {
  label: string;
  account_number: string;
  routing_number: string;
  next_check_number: number;
}

// Validation schema for bank account form
const bankAccountSchema = Yup.object().shape({
  label: Yup.string()
    .required("Label is required")
    .min(3, "Label must be at least 3 characters")
    .max(255, "Label must not exceed 255 characters"),
  account_number: Yup.string()
    .required("Account number is required")
    .matches(/^[0-9]+$/, "Account number must contain only digits")
    .min(4, "Account number must be at least 4 digits")
    .max(17, "Account number must not exceed 17 digits"),
  routing_number: Yup.string()
    .required("Routing number is required")
    .matches(/^[0-9]{9}$/, "Routing number must be exactly 9 digits"),
  next_check_number: Yup.number()
    .required("Next check number is required")
    .integer("Check number must be an integer")
    .min(1, "Check number must be at least 1"),
});

// Update validation schema (only allows updating label and next check number)
const bankAccountUpdateSchema = Yup.object().shape({
  label: Yup.string()
    .required("Label is required")
    .min(3, "Label must be at least 3 characters")
    .max(255, "Label must not exceed 255 characters"),
  next_check_number: Yup.number()
    .required("Next check number is required")
    .integer("Check number must be an integer")
    .min(1, "Check number must be at least 1"),
});

export function BankAccountCreateEdit({
  id,
  selectedBankAccount,
  isOpen,
  onClose,
}: IBankAccountEditProps): JSX.Element {
  const { triggerSnackbar } = useContext(SnackbarContext);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEditMode = id > 0 && selectedBankAccount !== null;

  const initialValues: IBankAccountForm = isEditMode
    ? {
        label: selectedBankAccount!.label,
        account_number: "", // Don't populate for security
        routing_number: "", // Don't populate for security
        next_check_number: selectedBankAccount!.next_check_number,
      }
    : {
        label: "",
        account_number: "",
        routing_number: "",
        next_check_number: 1,
      };

  const handleSubmit = async (values: IBankAccountForm) => {
    // console.log("Submitting bank account form with values:", values);
    setIsSubmitting(true);
    try {
      if (isEditMode) {
        // Update existing bank account (only label and next_check_number)
        await BankAccountService.updateBankAccount(id, {
          label: values.label,
          next_check_number: values.next_check_number,
        });
        triggerSnackbar("Bank account updated successfully", "success");
      } else {
        // Create new bank account
        // console.log("Creating bank account with values:", values);
        await BankAccountService.createBankAccount({
          label: values.label,
          account_number: values.account_number,
          routing_number: values.routing_number,
          next_check_number: values.next_check_number,
        });
        triggerSnackbar("Bank account created successfully", "success");
      }
      onClose();
    } catch (error: any) {
      console.error("Error saving bank account:", error);
      const errorMessage =
        error?.response?.data?.error ||
        error?.message ||
        "An error occurred while saving the bank account";
      triggerSnackbar(errorMessage, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <HeadlessDialog
      isOpen={isOpen}
      setIsOpen={onClose}
      onClose={onClose}
      title={isEditMode ? "Edit Bank Account" : "Create Bank Account"}
      description=""
      buttonText="Close"
    >
      <Formik
        initialValues={initialValues}
        validationSchema={
          isEditMode ? bankAccountUpdateSchema : bankAccountSchema
        }
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ errors, touched, values, isValid, dirty }) => {
          // console.log("Formik render - values:", values);
          // console.log("Formik render - errors:", errors);
          // console.log("Formik render - isValid:", isValid);
          // console.log("Formik render - dirty:", dirty);

          return (
            <Form>
              <ScrollableModalBody>
                <div className="tw-space-y-4">
                  <Field
                    as={FormikInput}
                    label="Account Label"
                    name="label"
                    type="text"
                    placeholder="e.g., Main Operating Account"
                    required
                  />

                  {!isEditMode && (
                    <>
                      <Field
                        as={FormikInput}
                        label="Account Number"
                        name="account_number"
                        type="text"
                        placeholder="Enter account number"
                        required
                      />

                      <Field
                        as={FormikInput}
                        label="Routing Number"
                        name="routing_number"
                        type="text"
                        placeholder="Enter 9-digit routing number"
                        required
                        maxLength={9}
                      />
                    </>
                  )}

                  {isEditMode && (
                    <div className="tw-bg-yellow-50 tw-border tw-border-yellow-200 tw-rounded tw-p-3 tw-text-sm">
                      <p className="tw-font-semibold tw-text-yellow-800">
                        Security Notice
                      </p>
                      <p className="tw-text-yellow-700">
                        Account and routing numbers cannot be modified for
                        security reasons. If you need to update these, please
                        create a new account.
                      </p>
                      <p className="tw-text-yellow-700 tw-mt-2">
                        Current Account:{" "}
                        {selectedBankAccount?.account_number_masked}
                      </p>
                      <p className="tw-text-yellow-700">
                        Current Routing:{" "}
                        {selectedBankAccount?.routing_number_masked}
                      </p>
                    </div>
                  )}

                  <Field
                    as={FormikInput}
                    label="Next Check Number"
                    name="next_check_number"
                    type="number"
                    placeholder="Enter starting check number"
                    required
                    min={1}
                  />
                </div>
              </ScrollableModalBody>

              <div className="tw-flex tw-justify-end tw-gap-2 tw-p-4 tw-border-t">
                {/* <Button
                  type="button"
                  onClick={onClose}
                  disabled={isSubmitting}
                  className="tw-px-4 tw-py-2 tw-bg-gray-200 tw-text-gray-700 tw-rounded hover:tw-bg-gray-300"
                >
                  Cancel
                </Button> */}
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  // onClick={() =>
                  //   console.log(
                  //     "Submit button clicked! isValid:",
                  //     isValid,
                  //     "errors:",
                  //     errors
                  //   )
                  // }
                  className="tw-px-4 tw-py-2 tw-bg-blue-600 tw-text-white tw-rounded hover:tw-bg-blue-700"
                >
                  {isSubmitting ? (
                    <div className="tw-flex tw-items-center tw-gap-2">
                      <Spinner className="tw-w-4 tw-h-4" />
                      <span>Saving...</span>
                    </div>
                  ) : isEditMode ? (
                    "Update"
                  ) : (
                    "Create"
                  )}
                </Button>
              </div>
            </Form>
          );
        }}
      </Formik>
    </HeadlessDialog>
  );
}
